import React, { useState, useEffect } from 'react';
import { X, Save, Sparkles, Tag, Eye, EyeOff, CheckCircle, Loader2, BarChart3 } from 'lucide-react';
import { chartsAPI } from '../services/api';

interface SaveChartModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  chartConfig: {
    type: string;
    indexName: string;
    xAxisField: string;
    yAxisField: string;
    seriesField?: string;
    aggregation: string;
    filters: { [key: string]: any };
    chartData: any[];
    totalResults: number;
    unit?: string;
    seriesToUnitMap?: Record<string, string>;
  };
  fields: Array<{ name: string; displayName: string }>;
  multipleCharts?: Array<{ title: string; data: any[]; filters: { [key: string]: any }; unit?: string }>;
}

const SaveChartModal: React.FC<SaveChartModalProps> = ({
  isOpen,
  onClose,
  onSave,
  chartConfig,
  fields,
  multipleCharts = [],
}) => {
  const [chartName, setChartName] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Labels lisibles pour les filtres
  const getFilterLabel = (key: string): string => {
    const field = fields.find(f => f.name === key || f.name.toLowerCase() === key.toLowerCase());
    if (field?.displayName) return field.displayName;
    const labels: Record<string, string> = {
      area: 'Area', element: 'Element', item: 'Item', domain: 'Domain',
      year: 'Year', unit: 'Unit', flag: 'Flag', value: 'Value', note: 'Note',
    };
    return labels[key.toLowerCase()] || key.charAt(0).toUpperCase() + key.slice(1);
  };

  const formatFilterValue = (value: any): string => {
    if (value == null || value === '') return '';
    if (Array.isArray(value)) return value.filter(v => v != null && v !== '').join(', ');
    return String(value);
  };

  const generateAutoDescription = () => {
    const lines: string[] = ['Filtres Appliqués:'];
    const filters = chartConfig.filters || {};
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value != null && value !== '' && (!Array.isArray(value) || value.length > 0)) {
        const label = getFilterLabel(key);
        const formatted = formatFilterValue(value);
        if (formatted) lines.push(`${label}: ${formatted}`);
      }
    });
    if (chartConfig.unit) {
      lines.push(`Unité: ${chartConfig.unit}`);
    }
    return lines.length > 1 ? lines.join('\n') : '';
  };

  // Réinitialiser l'état quand le modal se ferme
  useEffect(() => {
    if (!isOpen) {
      setChartName('');
      setDescription('');
      setSuccess(false);
      setError(null);
    }
  }, [isOpen]);

  // Auto-générer le nom, les tags et la description à l'ouverture
  useEffect(() => {
    if (isOpen && chartConfig) {
      generateAutoName();
      generateAutoTags();
      setDescription(generateAutoDescription());
    }
  }, [isOpen, chartConfig]);

  const generateAutoName = () => {
    const parts: string[] = [];
    
    // Ajouter le type de graphique
    const chartTypeNames: { [key: string]: string } = {
      bar: 'Barres',
      line: 'Lignes',
      pie: 'Secteurs',
      area: 'Aires',
      scatter: 'Nuage',
      radar: 'Radar',
      combo: 'Combo',
      table: 'Tableau',
    };
    parts.push(chartTypeNames[chartConfig.type] || chartConfig.type);

    // Ajouter l'index
    if (chartConfig.indexName) {
      const indexDisplay = chartConfig.indexName
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());
      parts.push(indexDisplay);
    }

    // Ajouter les axes principaux
    if (chartConfig.xAxisField && chartConfig.yAxisField) {
      const xField = fields.find(f => f.name === chartConfig.xAxisField);
      const yField = fields.find(f => f.name === chartConfig.yAxisField);
      if (xField && yField) {
        parts.push(`${xField.displayName} × ${yField.displayName}`);
      }
    }

    // Ajouter la date actuelle
    const date = new Date().toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit' 
    });
    
    setChartName(parts.join(' - ') + ` (${date})`);
  };

  const generateAutoTags = () => {
    const autoTags: string[] = [];

    // Tag basé sur le type de graphique
    autoTags.push(chartConfig.type);

    // Tags basés sur les filtres actifs
    Object.keys(chartConfig.filters).forEach((key) => {
      const value = chartConfig.filters[key];
      if (value) {
        const field = fields.find(f => f.name === key);
        if (field) {
          autoTags.push(field.name.toLowerCase());
        }
      }
    });

    // Tag basé sur l'index
    if (chartConfig.indexName) {
      const indexTag = chartConfig.indexName.split('_')[0];
      if (indexTag) {
        autoTags.push(indexTag.toLowerCase());
      }
    }

    // Tags basés sur les axes
    if (chartConfig.xAxisField) {
      const xField = fields.find(f => f.name === chartConfig.xAxisField);
      if (xField) {
        autoTags.push(xField.name.toLowerCase());
      }
    }
    if (chartConfig.yAxisField) {
      const yField = fields.find(f => f.name === chartConfig.yAxisField);
      if (yField) {
        autoTags.push(yField.name.toLowerCase());
      }
    }

    // Dédupliquer et limiter
    setTags(Array.from(new Set(autoTags)).slice(0, 5));
  };

  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      const newTag = tagInput.trim().toLowerCase();
      if (!tags.includes(newTag)) {
        setTags([...tags, newTag]);
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleSave = async () => {
    if (!chartName.trim()) {
      setError('Le nom du graphique est requis');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Si on a plusieurs graphiques, utiliser l'API batch
      if (multipleCharts && multipleCharts.length > 0) {
        console.log(`[SaveChartModal] 💾 Saving ${multipleCharts.length} charts in batch`);
        
        // Préparer les configurations pour chaque graphique
        const chartsData = multipleCharts.map((chartInfo) => {
          // S'assurer que les filtres sont un objet valide
          let filtersToUse = chartInfo.filters || {};
          if (typeof filtersToUse !== 'object' || Array.isArray(filtersToUse)) {
            console.warn('[SaveChartModal] ⚠️ Invalid filters type, using empty object');
            filtersToUse = {};
          }
          
          // Fusionner avec les filtres globaux si nécessaire (les filtres de chartInfo devraient déjà être complets)
          // Mais on s'assure qu'ils ne sont pas vides
          if (Object.keys(filtersToUse).length === 0 && Object.keys(chartConfig.filters || {}).length > 0) {
            console.warn('[SaveChartModal] ⚠️ Chart filters are empty, using global filters as fallback');
            filtersToUse = chartConfig.filters;
          }
          
          console.log(`[SaveChartModal] 📋 Chart "${chartInfo.title}" filters:`, JSON.stringify(filtersToUse, null, 2));
          
          return {
            title: chartInfo.title,
            type: chartConfig.type,
            indexName: chartConfig.indexName,
            description: description.trim() || undefined,
            dataConfig: {
              xAxis: {
                field: chartConfig.xAxisField,
                label: fields.find(f => f.name === chartConfig.xAxisField)?.displayName || chartConfig.xAxisField,
              },
              yAxis: chartConfig.yAxisField ? {
                field: chartConfig.yAxisField,
                label: fields.find(f => f.name === chartConfig.yAxisField)?.displayName || chartConfig.yAxisField,
                aggregation: chartConfig.aggregation,
              } : undefined,
              series: chartConfig.seriesField ? [{
                field: chartConfig.seriesField,
                label: fields.find(f => f.name === chartConfig.seriesField)?.displayName || chartConfig.seriesField,
              }] : [],
              unit: chartInfo.unit || '',
            },
            filters: filtersToUse, // Utiliser les filtres spécifiques à chaque graphique
            visualization: {
              showLegend: true,
              showGrid: true,
              stacked: false,
              smooth: false,
            },
            tags: tags,
            isPublic: isPublic,
          };
        });

        const response = await chartsAPI.createMultipleCharts({
          charts: chartsData,
          baseName: chartName.trim(),
          description: description.trim() || undefined,
          tags: tags,
          isPublic: isPublic,
        });

        console.log('[SaveChartModal] 💾 Batch save response:', response.data);

        if (response.data.success) {
          setSuccess(true);
          setTimeout(() => {
            onSave();
            onClose();
          }, 1500);
        } else {
          setError(response.data.message || 'Erreur lors de la sauvegarde des graphiques');
        }
      } else {
        // Sauvegarder un seul graphique (comportement original)
        const chartData = {
          name: chartName.trim(),
          type: chartConfig.type,
          indexName: chartConfig.indexName,
          description: description.trim() || undefined,
          dataConfig: {
            xAxis: {
              field: chartConfig.xAxisField,
              label: fields.find(f => f.name === chartConfig.xAxisField)?.displayName || chartConfig.xAxisField,
            },
            yAxis: chartConfig.yAxisField ? {
              field: chartConfig.yAxisField,
              label: fields.find(f => f.name === chartConfig.yAxisField)?.displayName || chartConfig.yAxisField,
              aggregation: chartConfig.aggregation,
            } : undefined,
            series: chartConfig.seriesField ? [{
              field: chartConfig.seriesField,
              label: fields.find(f => f.name === chartConfig.seriesField)?.displayName || chartConfig.seriesField,
            }] : [],
            unit: chartConfig.unit || '',
            seriesToUnitMap: chartConfig.seriesToUnitMap || {},
          },
          filters: chartConfig.filters,
          visualization: {
            showLegend: true,
            showGrid: true,
            stacked: false,
            smooth: false,
          },
          tags: tags,
          isPublic: isPublic,
        };

        console.log('[SaveChartModal] 💾 Saving single chart with data:');
        console.log('[SaveChartModal] 💾 chartData:', JSON.stringify(chartData, null, 2));

        const response = await chartsAPI.createChart(chartData);
        
        console.log('[SaveChartModal] 💾 Save response:', response.data);

        if (response.data.success) {
          setSuccess(true);
          setTimeout(() => {
            onSave();
            onClose();
          }, 1500);
        } else {
          setError(response.data.message || 'Erreur lors de la sauvegarde');
        }
      }
    } catch (err: any) {
      console.error('Error saving chart(s):', err);
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde du graphique');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white border border-gray-200 rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header - light template */}
        <div className="sticky top-0 bg-emerald-50/80 border-b border-emerald-200/60 p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-emerald-600 rounded-lg">
                <Save className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Enregistrer le Graphique</h2>
                <p className="text-sm text-gray-600">Sauvegardez cette configuration pour la réutiliser plus tard</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors text-gray-600 hover:text-gray-900"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content - light mode */}
        <div className="p-6 space-y-6 bg-white">
          {success ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center animate-pulse">
                  <CheckCircle className="h-10 w-10 text-white" />
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {multipleCharts && multipleCharts.length > 0 
                    ? `${multipleCharts.length} Graphiques enregistrés !`
                    : 'Graphique enregistré !'}
                </h3>
                <p className="text-gray-600 mb-4">
                  {multipleCharts && multipleCharts.length > 0
                    ? `Vos ${multipleCharts.length} configurations ont été sauvegardées avec succès`
                    : 'Votre configuration a été sauvegardée avec succès'}
                </p>
                <button
                  onClick={() => {
                    onClose();
                    window.location.href = '/charts';
                  }}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all text-sm font-medium"
                >
                  Voir mes graphiques
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Prévisualisation */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <BarChart3 className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm font-medium text-gray-900">
                    {multipleCharts && multipleCharts.length > 0 
                      ? `Aperçu de ${multipleCharts.length} graphiques`
                      : 'Aperçu de la configuration'}
                  </span>
                </div>
                {multipleCharts && multipleCharts.length > 0 ? (
                  <div className="space-y-2 text-sm">
                    <div className="grid grid-cols-2 gap-3">
                      <div><span className="text-gray-500">Type:</span><span className="ml-2 text-gray-900 capitalize">{chartConfig.type}</span></div>
                      <div><span className="text-gray-500">Index:</span><span className="ml-2 text-gray-900">{chartConfig.indexName}</span></div>
                      <div><span className="text-gray-500">Axe X:</span><span className="ml-2 text-gray-900">{fields.find(f => f.name === chartConfig.xAxisField)?.displayName || chartConfig.xAxisField}</span></div>
                      <div><span className="text-gray-500">Axe Y:</span><span className="ml-2 text-gray-900">{fields.find(f => f.name === chartConfig.yAxisField)?.displayName || chartConfig.yAxisField}</span></div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <span className="text-gray-500">Graphiques à enregistrer:</span>
                      <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                        {multipleCharts.map((chart, idx) => (
                          <div key={idx} className="text-xs text-gray-700 bg-white border border-gray-200 p-2 rounded">{idx + 1}. {chart.title}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-gray-500">Type:</span><span className="ml-2 text-gray-900 capitalize">{chartConfig.type}</span></div>
                    <div><span className="text-gray-500">Index:</span><span className="ml-2 text-gray-900">{chartConfig.indexName}</span></div>
                    <div><span className="text-gray-500">Axe X:</span><span className="ml-2 text-gray-900">{fields.find(f => f.name === chartConfig.xAxisField)?.displayName || chartConfig.xAxisField}</span></div>
                    <div><span className="text-gray-500">Axe Y:</span><span className="ml-2 text-gray-900">{fields.find(f => f.name === chartConfig.yAxisField)?.displayName || chartConfig.yAxisField}</span></div>
                    <div className="col-span-2"><span className="text-gray-500">Résultats:</span><span className="ml-2 text-gray-900">{chartConfig.totalResults.toLocaleString()} données</span></div>
                  </div>
                )}
              </div>

              {/* Nom du graphique */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nom du graphique *</label>
                <input
                  type="text"
                  value={chartName}
                  onChange={(e) => setChartName(e.target.value)}
                  placeholder="Entrez un nom pour ce graphique"
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-gray-900 placeholder-gray-400"
                />
                <p className="mt-1 text-xs text-gray-500">💡 Le nom a été généré automatiquement, vous pouvez le modifier</p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description (optionnel)</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Ajoutez une description pour mieux identifier ce graphique..."
                  rows={3}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-gray-900 placeholder-gray-400 resize-none"
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2"><Tag className="inline h-4 w-4 mr-1" /> Tags</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {tags.map((tag) => (
                    <span key={tag} className="inline-flex items-center px-3 py-1 bg-emerald-100 border border-emerald-200 rounded-full text-sm text-emerald-800">
                      {tag}
                      <button onClick={() => handleRemoveTag(tag)} className="ml-2 hover:text-red-600 transition-colors text-emerald-700"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleAddTag}
                  placeholder="Appuyez sur Entrée pour ajouter un tag"
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-gray-900 placeholder-gray-400"
                />
                <p className="mt-1 text-xs text-gray-500">💡 Tags suggérés automatiquement basés sur votre configuration</p>
              </div>

              {/* Visibilité */}
              <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  {isPublic ? <Eye className="h-5 w-5 text-emerald-600" /> : <EyeOff className="h-5 w-5 text-gray-500" />}
                  <div>
                    <div className="text-sm font-medium text-gray-900">{isPublic ? 'Graphique public' : 'Graphique privé'}</div>
                    <div className="text-xs text-gray-600">{isPublic ? 'Visible par tous les utilisateurs' : 'Visible uniquement par vous'}</div>
                  </div>
                </div>
                <button onClick={() => setIsPublic(!isPublic)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isPublic ? 'bg-emerald-600' : 'bg-gray-300'}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${isPublic ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
                  <X className="h-5 w-5 text-red-600" />
                  <span className="text-sm text-red-700">{error}</span>
                </div>
              )}

              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                <button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">Annuler</button>
                <button
                  onClick={handleSave}
                  disabled={saving || !chartName.trim()}
                  className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {saving ? (<><Loader2 className="h-4 w-4 animate-spin" /><span>Enregistrement...</span></>) : (<><Save className="h-4 w-4" /><span>Enregistrer</span></>)}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SaveChartModal;
